import React from 'react';
import PropTypes from 'prop-types';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  withStyles,
  IconButton,
  styled,
} from '@material-ui/core';
import { AddCircle, RemoveCircle } from '@material-ui/icons';

const propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      align: PropTypes.oneOf(['center', 'left', 'right']),
      header: PropTypes.string.isRequired,
    })
  ).isRequired,
  includedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  excludedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  actionColumnWidth: PropTypes.number,
};
const defaultProps = {
  disabled: false,
  onChange: undefined,
  actionColumnWidth: 60,
};

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#CCD6E1',
    borderBottom: 'none',
    color: '#042440',
    padding: '6px 0px 6px 8px',
    textTransform: 'uppercase',
    '&:last-child': {
      paddingRight: '8px',
    },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    borderBottom: 'none',
    color: '#042440',
    padding: '3px 0px 3px 8px',
    '&:last-child': {
      paddingRight: '8px',
    },
  },
}))(TableCell);

const StyledExcludedTableCell = withStyles(() => ({
  body: {
    borderBottom: 'none',
    color: '#A2A6CC',
    fontStyle: 'italic',
    padding: '3px 0px 3px 8px',
    '&:last-child': {
      paddingRight: '8px',
    },
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#FFFFFF',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#F1EBF4',
    },
  },
}))(TableRow);

const StyledExcludedTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#F0F0F7',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#FFFFFF',
    },
  },
}))(TableRow);

const StyledIconButton = withStyles(() => ({
  root: {
    color: '#545CA1',
  },
}))(IconButton);

const TableCellDivider = withStyles(() => ({
  body: {
    borderBottom: 'none',
    padding: '8px 0',
    '&:last-child': {
      paddingRight: '0',
    },
  },
}))(TableCell);

const StyledDivider = withStyles(() => ({
  root: {
    backgroundColor: '#D9D9D9',
  },
}))(Divider);

const StyledTable = styled(Table)(() => ({
  tableLayout: 'fixed',
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  overflowX: 'hidden',
}));

function TransferTable({ disabled, columns, includedItems, excludedItems, onChange, actionColumnWidth }) {
  const handleInclude = id => {
    const item = excludedItems.find(row => row.id === id);
    const newExcludedItems = excludedItems.filter(row => row.id !== id);
    const newIncludedItems = [...includedItems, item];
    onChange(newIncludedItems, newExcludedItems);
  };

  const handleExclude = id => {
    const item = includedItems.find(row => row.id === id);
    const newIncludedItems = includedItems.filter(row => row.id !== id);
    const newExcludedItems = [...excludedItems, item];
    onChange(newIncludedItems, newExcludedItems);
  };

  return (
    <StyledTableContainer>
      <StyledTable aria-label="simple table" size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell align="center" width={actionColumnWidth} />
            {columns.map(column => (
              <StyledTableCell key={column.id} align={column.align}>
                {column.header}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {includedItems.map(row => (
            <StyledTableRow key={row.id}>
              <StyledTableCell align="center" width={actionColumnWidth}>
                <StyledIconButton
                  aria-label="Remove from list"
                  disabled={disabled}
                  size="small"
                  onClick={() => handleExclude(row.id)}
                >
                  <RemoveCircle />
                </StyledIconButton>
              </StyledTableCell>

              {columns.map(column => (
                <StyledTableCell key={column.id} align={column.align} width={column.width}>
                  {row[column.id]}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}

          <TableRow>
            <TableCellDivider colSpan={6}>
              <StyledDivider />
            </TableCellDivider>
          </TableRow>

          {excludedItems.map(row => (
            <StyledExcludedTableRow key={row.id}>
              <StyledExcludedTableCell align="center" width={actionColumnWidth}>
                <StyledIconButton
                  aria-label="Add to list"
                  disabled={disabled}
                  onClick={() => handleInclude(row.id)}
                  size="small"
                >
                  <AddCircle />
                </StyledIconButton>
              </StyledExcludedTableCell>

              {columns.map(column => (
                <StyledExcludedTableCell key={column.id} align={column.align} width={column.width}>
                  {row[column.id]}
                </StyledExcludedTableCell>
              ))}
            </StyledExcludedTableRow>
          ))}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
}

TransferTable.propTypes = propTypes;
TransferTable.defaultProps = defaultProps;

export default TransferTable;
